<template>
	<form v-if="filtersData" class="ui form" @submit.prevent="onSubmit">
		<div v-if="filtersGroup.length" class="ui fluid basic buttons" style="margin-bottom: 1rem;">
			<div
				:key="`gf0${index}`"
				v-for="(group, index) in filtersGroup"
				:class="['ui', {active: group === currentGroup}, 'button']"
				@click="() => updateCurrentGroup(group)"
			>{{group}}
			</div>
		</div>
		<template v-for="(fg, index) in filtersDataSource">
			<template v-for="(fd, i) in fg.fields">
				<kendo-multiselect
					v-if="fd.type === 'multiselect' && !fd.hide"
					v-model="fd.values"
					:key="`gf1${index}${i}${fd.title}`"
					:dataSource="fd.dataSource"
					:dataTextField="fd.text"
					:dataValueField="fd.value"
					:description="fd.description"
					:enable="fd.enable"
					:index="fd.index"
					:label="fd.title"
					:name="getKey(fd)"
					:placeholder="fd.placeholder === undefined ? fd.required ? i18n('Action.Select') : getPlaceholder(fd.title) : fd.placeholder"
					:required="fd.required"
					@change="updateDataSources(fg, i)"
				></kendo-multiselect>
				<kendo-dropdown
					v-else-if="fd.type === 'dropdown' && !fd.hide"
					v-model="fd.values"
					:key="`gf2${index}${i}${fd.title}`"
					:dataSource="fd.dataSource"
					:dataTextField="fd.text"
					:dataValueField="fd.value"
					:description="fd.description"
					:enable="fd.enable"
					:index="fd.index"
					:label="fd.title"
					:name="getKey(fd)"
					:placeholder="i18n('Action.Select')"
					:required="fd.required"
					@change="updateDataSources(fg, i)"
				></kendo-dropdown>
				<kendo-datepicker
					v-else-if="fd.type === 'datepicker' && !fd.hide"
					v-model="fd.values"
					:key="`gf3${index}${i}${fd.title}`"
					:dataSource="!fd.range ? fd.dataSource : null"
					:dataTextField="fd.text"
					:dataValueField="fd.value"
					:description="fd.description"
					:enable="fd.enable"
					:format="fd.format"
					:inputFormat="fd.inputFormat"
					:label="fd.title"
					:max="fd.range && fd.dataSource ? getMaxDateFromDataSource(fd.dataSource, fd.value) : true"
					:min="fd.range && fd.dataSource ? getMinDateFromDataSource(fd.dataSource, fd.value) : true"
					:outputFormat="fd.outputFormat"
					:required="fd.required"
				></kendo-datepicker>
				<kendo-slider
					v-else-if="!fd.hide"
					v-model="fd.values"
					:key="`gf4${index}${i}${fd.title}`"
					:dataSource="fd.dataSource"
					:dataTextField="fd.text"
					:dataValueField="fd.value"
					:description="fd.description"
					:enable="fd.enable"
					:hint="true"
					:label="fd.title"
					:largeStep="0"
					:range="!!fd.range"
					:required="fd.required"
				></kendo-slider>
			</template>
		</template>
		<div v-show="errors.length" class="ui red tiny message">
			<ul class="list">
				<li :key="`gf5${index}`" v-for="(error, index) in errors" v-html="error"></li>
			</ul>
		</div>
		<button class="fluid ui primary button" type="submit">{{ i18n('Action.Filter') }}</button>
	</form>
</template>

<script>
  import _ from 'lodash';
  import moment from 'moment';
  import {mapActions, mapGetters} from 'vuex';

  import KendoDatepicker from '@/components/kendo-datepicker/kendo-datepicker';
  import KendoDropdown from '@/components/kendo-dropdown/kendo-dropdown';
  import KendoMultiselect from '@/components/kendo-multiselect/kendo-multiselect';
  import KendoSlider from '@/components/kendo-slider/kendo-slider';

  import {i18nMixin} from '@/modules/neodash/mixins';
  import {httpService} from '@/services';

  import {CONFIG} from '@/config';
  import {DISABLE_LOADING, ENABLE_LOADING} from '@/store/types';
  import {
    FILTERS_GET_CATEGORIES,
    FILTERS_GET_CURRENT_CATEGORY,
    FILTERS_GET_CURRENT_GROUP,
    FILTERS_GET_DATA,
    FILTERS_GET_PARAMS,
    FILTERS_GET_VALUES,
    FILTERS_UPDATE_CURRENT_GROUP,
    FILTERS_UPDATE_VALUES
  } from '@/modules/neodash/store/modules/filters/types';
  import {constants} from 'zlib';
  import {runInDebugContext} from 'vm';

  export default {
    props: {
      name: String,
      values: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        errors: [],
        filtersDataSource: [],
        filtersSummary: '',
        buildingFilters: null
      };
    },

    computed: {
      ...mapGetters({
        requestParams: FILTERS_GET_PARAMS,
        filtersData: FILTERS_GET_DATA,
        filtersValues: FILTERS_GET_VALUES,
        filtersCategories: FILTERS_GET_CATEGORIES,
        filtersCurrentCategory: FILTERS_GET_CURRENT_CATEGORY,
        filtersCurrentGroup: FILTERS_GET_CURRENT_GROUP
      }),

      filtersGroup() {
        return _(this.filtersData)
            .map(fg => fg.group)
            .flatMap()
            .uniq()
            .filter(g => g)
            .sort()
            .value();
      },

      currentGroup() {
        return this.filtersCurrentGroup ? this.filtersCurrentGroup : this.filtersGroup[0];
      }
    },

    mixins: [i18nMixin],

    methods: {
      ...mapActions({
        updateFiltersValues: FILTERS_UPDATE_VALUES,
        updateCurrentGroup: FILTERS_UPDATE_CURRENT_GROUP,
        enableLoading: ENABLE_LOADING,
        disableLoading: DISABLE_LOADING
      }),

      reload() {
        this.createFiltersGroup();
      },

      isValid() {
        this.errors = [];
        return _.reduce(
            this.filtersDataSource,
            (acc, fg) => {
              _.forEach(fg.fields, fd => {
                if (fd.required && (!_.size(fd.values) || fd.values.value == undefined || fd.values.value == null || fd.values.text == 'Selecione')) {
                  this.errors.push(`<strong>${fd.title}</strong> é necessário.`);
                  acc = false;
                }
              });
              return acc;
            },
            true
        );
      },

      getKey(field) {
        return field.name || _.snakeCase(_.deburr(field.title));
      },

      getPlaceholder(text, suffix) {
        return _.some(suffix, t => text.includes(t, text.length - 3)) ? 'Todas…' : 'Todos…';
      },

      hasValue(value) {
        return value != null && (_.isArray(value) ? _.size(value) : !isNaN(Number(value)) ? true : (value != null && value != undefined));
      },

      normalize(value, type) {
        // IS A STRING
        if (type === 'string') {
          return _.deburr(value).toUpperCase();
          // IS A DATE
        } else if (value instanceof Date) {
          return moment(value)
              .add(new Date().getTimezoneOffset() / 60, 'hours')
              .format('YYYYMMDD');
          // IS A NUMBER
        } else if (!isNaN(Number(value))) {
          return Number(value);
          // IS A DATE AS TEXT
        } else if (Date.parse(value)) {
          const date = new Date(value);

          return moment(date)
              .add(new Date().getTimezoneOffset() / 60, 'hours')
              .format('YYYYMMDD');
        }

        // IS A STRING (PROBABLY)
        return _.deburr(value).toUpperCase();
      },

      getMaxDateFromDataSource(dataSource, prop) {
        return _(dataSource)
            .map(data => {
              const now = new Date(data[prop]);
              const timezoneOffset = new Date().getTimezoneOffset() / 60;
              return moment(now)
                  .add(timezoneOffset, 'hours')
                  .toDate();
            })
            .max();
      },

      getMinDateFromDataSource(dataSource, prop) {
        return _(dataSource)
            .map(data => {
              const now = new Date(data[prop]);
              const timezoneOffset = new Date().getTimezoneOffset() / 60;
              return moment(now)
                  .add(timezoneOffset, 'hours')
                  .toDate();
            })
            .min();
      },

      createFiltersGroup() {
        const resetTimeout = () => {
          clearTimeout(this.buildingFilters);
          this.buildingFilters = undefined;
        };

        if (this.buildingFilters) {
          resetTimeout();
        }

        this.buildingFilters = setTimeout(async () => {
          // console.time('FilterControl->createFiltersGroup');
          const filtersDataSource = _(_.cloneDeep(this.filtersData))
              .map(fg => {
                fg.fields = _.filter(fg.fields, fd => {
                  if (_.size(fd.visible)) {
                    return _.some(fd.visible, id => _.some([this.name, this.filtersCurrentCategory], name => id === name));
                  } else if (_.size(fd.hidden)) {
                    return !_.some(fd.hidden, id => _.some([this.name, this.filtersCurrentCategory], name => id === name));
                  }
                  return true;
                });
                return fg;
              })
              .filter(fg => _.size(fg.fields))
              .filter(fg => !(_.has(fg, 'group') && !_.some(fg.group, g => g === this.currentGroup)))
              .orderBy(fg => fg.position)
              .value();
          this.$set(this, 'filtersDataSource', await this.populateFiltersGroup(filtersDataSource));
          resetTimeout();
          // console.timeEnd('FilterControl->createFiltersGroup');
        }, 100);
      },

      getFieldsDependencies(filtersDataSource, filtersGroup) {
        return _(filtersDataSource)
            .filter(fg => fg.name === filtersGroup.dependency)
            .map(fg => fg.fields)
            .flatten()
            .map(fd => this.getKey(fd))
            .value();
      },

      getFieldsDependenciesHidden(filtersDataSource, filtersGroup) {
        return _(filtersDataSource)
            .filter(fg => fg.name === filtersGroup.dependency)
            .map(fg => fg.fields)
            .flatten()
            .map(fd => this.getKey(fd))
            .value();
      },

      async populateFiltersGroup(filtersDataSource) {
        // console.time('FilterControl->populateFiltersGroup');
        const filtersValues = _.assign({}, this.filtersValues);

        const queue = _.map(filtersDataSource, async fg => {
          if (fg.queryUrl) {
            fg.data = await this.requestFilterGroupData(fg, filtersValues);
          }

          fg.fields = _.map(fg.fields, (fd, i) => {

            const fieldDependencies = this.getFieldsDependencies(filtersDataSource, fg);
            // check if there is a dependency filter and if every dependency is fulfilled
            if (_.has(fg, 'hiddenConditional.dependency')) {
              const hdFilter = fg.hiddenConditional;
              const filterPrincipal = filtersValues[hdFilter.filter];
              if (!_.isUndefined(filterPrincipal)) {
                fd.enable =
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value);
                fd.required =
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value);
                fg.hiddenConditional.state = !(
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value)
                );
              }
            }

            if (_.has(fd, 'visibleConditional.dependency')) {
              const hdFilter = fd.visibleConditional;
              const fitlerPrincipalList = _(filtersDataSource).filter(f => f.name == hdFilter.dependency).map(flData => flData.fields).flatten().value();

              if (!_.isUndefined(fitlerPrincipalList)) {
                const filterPrincipal = fitlerPrincipalList[0];
                fd.enable = (Number(filterPrincipal.values.value) >= Number(hdFilter.valueMax));

                fd.hide = fd.enable ? false : true;
              }
            }

            if (_.has(fg, 'dependency') && !_.every(fieldDependencies, p => filtersValues[p])) {
              fd.enable = false;
            }
            if (fg.data && (!fd.dataSource || !_.size(fd.dataSource))) {
              fd.dataSource = this.formatDataSource(this.filterDataSourceInit(fg.data, fg.fields, fd), fd);
            }

            if (i > 0) {
              // Get filters that are in the upper filters
              fd.dataSource = this.createFilteredDataSource(fg, fd, this.getValidFilters(fg, filtersValues, i), true);
            }

            fd.values = this.getFieldValues(fd);

            return fd;
          });

          return fg;
        });

        return await Promise.all(queue);
        // console.timeEnd('FilterControl->populateFiltersGroup');
      },

      requestFilterGroupData(filtersGroup, filtersValues) {
        try {
          let url = _.replace(filtersGroup.queryUrl.url, '{apiUrl}', CONFIG.URL.NEODASH_API);

          if (filtersValues) {
            _.forEach(filtersGroup.queryUrl.params, p => {
              const regex = new RegExp(`{${p}}`, 'g');

              if (_.has(filtersGroup.queryUrl, 'paramList') && filtersGroup.queryUrl.paramList == p) {
                const paramList = filtersGroup.queryUrl.paramList;
                let params = [];
                if (_.has(filtersValues, p)) {
                  _.forEach(filtersValues[p].value, p => {
                    params.push(`${paramList}=${p}`);
                  });
                }

                const _parameters = params.length > 0 ? `?${params.join('&')}` : '';
                url = _.replace(url, regex, _parameters);
              } else {
                const value = filtersValues[p].value;
                url = _.replace(url, regex, value);
              }
            });
          }

          return httpService.http
              .get(url)
              .then(res => res.data)
              .catch(() => []);
        } catch (err) {
          return new Promise(resolve => {
            resolve([]);
          });
        }
      },

      getFieldValues(field, filtersValues) {
        // console.time('FilterControl->getFieldValues');
        try {
          const values = _.assign(
              {},
              {
                metadata: {
                  name: this.getKey(field),
                  text: field.text,
                  value: field.value == 0 ? undefined : field.value,
                  properties: Object.keys((field.dataSource && field.dataSource[0]) || {}),
                  all: false
                },
                value: field.valueDefault != undefined ? field.valueDefault : undefined,
              },
              this.filtersValues[this.getKey(field)] ||
              this.values[this.getKey(field)] ||
              (filtersValues && filtersValues[this.getKey(field)])
          );

          const getValue = dataSource => {
            switch (field.type) {
              case 'datepicker':
                return moment(dataSource[0] ? new Date(dataSource[0][field.value]) : new Date(values.value))
                    .add(new Date().getTimezoneOffset() / 60, 'hours')
                    .toDate();
              case 'dropdown':
                return _.isArray(dataSource) ? dataSource[0][field.value] : values.value;
              default:
                return _.map(dataSource, data => data[field.value]);
            }
          };

          const getText = dataSource => {
            switch (field.type) {
              case 'datepicker':
              case 'dropdown':
                return _.isArray(dataSource) ? dataSource[0][field.text] : values.text;
              default:
                return _.map(dataSource, data => data[field.text]);
            }
          };

          if (values.text && values.value != null && values.value != undefined) {
            const dataSource = _(field.dataSource)
                .filter(data =>
                    _.some(['value', 'text'], prop =>
                        this.hasValue(values[prop]) && _.isArray(values[prop])
                            ? _.some(
                            values[prop],
                            name =>
                                this.normalize(data[field[prop]], prop === 'text' ? 'string' : undefined) ===
                                this.normalize(name, prop === 'text' ? 'string' : undefined)
                            )
                            : this.normalize(data[field[prop]]) ===
                            this.normalize(values[prop], prop === 'text' ? 'string' : undefined)
                    )
                )
                .value();

            if (_.size(dataSource)) {
              values.value = getValue(dataSource);
              values.text = getText(dataSource);
            } else {
              values = {};
            }
          }

          return values;
        } catch (err) {
          console.log('ERROR: ', err, field);
          return {};
        }
        // console.timeEnd('FilterControl->getFieldValues');
      },

      formatDataSource(dataSource, field) {
        if (_.size(dataSource)) {
          const sortOrder = field.orderBy && field.orderBy[0] === '!' ? 'desc' : 'asc';
          const orderByProperty =
              field.orderBy && field.orderBy[0] === '!' ? field.orderBy.substr(1) : field.orderBy || 'text';
          return _(dataSource)
              .uniqBy(field.value)
              .orderBy(data => data[field[orderByProperty]], sortOrder)
              .value();
        }
        return [];
      },

      filterDataSource(dataSource, filtersGroup) {
        return _.filter(dataSource, data => {
          return _.every(filtersGroup, fd => {
            if (_.isArray(fd.value)) {
              if (_.size(fd.value)) {
                return _.some(fd.value, val => String(val) === String(data[fd.metadata.value]));
              }
              return true;
            } else if (this.hasValue(fd.value)) {
              return String(fd.value) === String(data[fd.metadata.value]);
            }

            return true;
          });
        });
      },

      filterDataSourceInit(dataSource, filtersGroup, fild) {
        return _.filter(dataSource, data => {
          return _.every(filtersGroup, fd => {
            if (this.hasValue(fd.valueDefault) && fd.value != fild.value) {
              return fd.value == fild.value || String(fd.valueDefault) === String(data[fd.value]);
              ;
            }
            return true;
          });
        });
      },

      createFilteredDataSource(filtersGroup, field, filtersValues, init) {

        // const data = Object.keys(filtersValues).length > 0 ? filtersGroup.data : field.dataSource;
        const data = init ? field.dataSource : filtersGroup.data;
        //const data = filtersGroup.data;
        const output = this.formatDataSource(this.filterDataSource(data, filtersValues), field);
        // console.timeEnd('FilterControl->createFilteredDataSource');
        return output;
      },

      getValidFilters(filtersGroup, filtersValues, currentIndex) {
        var fields = _(filtersGroup.fields)
            .filter((fd, i) => i <= currentIndex - 1)
            .map(fd => this.getKey(fd))
            .value();

        return _.reduce(
            filtersValues,
            (acc, val, key) => {
              if (_.some(fields, k => k === key)) {
                acc[key] = val;
              }
              return acc;
            },
            {}
        );
      },

      async updateDataSources(filtersGroup, index) {
        const filtersValues = await this.getFiltersValues(false);

        const queue = _.map(this.filtersDataSource, async fg => {
          fg.fields = _.map(fg.fields, fd => {
            if (_.has(fg, 'dependency') &&
                _.has(fg, 'hiddenConditional.dependency') &&
                _.isEqual(fg.hiddenConditional.dependency, filtersGroup.name)) {

              const hdFilter = fg.hiddenConditional;
              const filterPrincipal = filtersValues[hdFilter.filter];

              if (!_.isUndefined(filterPrincipal)) {
                fd.enable =
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value);
                fd.required =
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value);
                fg.hiddenConditional.state = !(
                    (hdFilter.equal && String(filterPrincipal.value) === hdFilter.value) ||
                    (!hdFilter.equal && String(filterPrincipal.value) !== hdFilter.value)
                );
              }
            }

            if (_.has(fd, 'visibleConditional.dependency')) {
              const hdFilter = fd.visibleConditional;
              const fitlerPrincipalList = filtersGroup.name == hdFilter.dependency ? filtersGroup.fields : undefined;
              if (!_.isUndefined(fitlerPrincipalList)) {
                const filterPrincipal = fitlerPrincipalList[0];
                fd.enable = (Number(filterPrincipal.values.value) >= Number(hdFilter.valueMax));

                if (fd.enable) {
                  fd.hide = false;
                } else {

                  if (fd.values.value == 'object') {
                    fd.values.value = [];
                  } else {
                    fd.values.value = undefined;
                  }

                  fd.hide = true;
                }
              }
            }

            return fd;
          });

          if (
              _.has(fg, 'dependency') &&
              _.isEqual(fg.dependency, filtersGroup.name) &&
              (_.every(this.getFieldsDependencies(this.filtersDataSource, fg), p => filtersValues[p]) ||
                  (_.has(fg, 'queryUrl') && _.has(fg.queryUrl, 'paramList')))
          ) {
            fg.data = await this.requestFilterGroupData(fg, filtersValues);
            fg.fields = _.map(fg.fields, fd => {
              if (
                  (_.has(fg, 'hiddenConditional.dependency') && fg.hiddenConditional.state == false) ||
                  !_.has(fg, 'hiddenConditional.dependency')
              ) {
                fd.dataSource = this.formatDataSource(fg.data, fd);
                fd.enable = !(fd.dataSource.length == 0);
              }

              return fd;
            });
          }

          if (fg.name !== filtersGroup.name) {
            return fg;
          }

          fg.fields = _.map(fg.fields, (fd, i) => {
            if (i <= index) {
              return fd;
            }
            // Get filters that are in the upper filters
            fd.dataSource = this.createFilteredDataSource(fg, fd, this.getValidFilters(fg, filtersValues, i), false);

            fd.values = this.getFieldValues(fd, filtersValues);

            return fd;
          });

          if (filtersGroup.fields.some(elem => elem.dependency)) {
            function disabledInput(fd) {
              if (fd.dependency) {
                fd.enable = fg.fields.some(elem => elem.title == fd.dependency && elem.values && elem.values.value);
                if (fd.enable == false && fd.values && fd.values.value) {
                  if (typeof fd.values.value == 'object') {
                    fd.values.value = [];
                  } else {
                    fd.values.value = undefined;
                  }
                }
              }
            }

            fg.fields.forEach(function (fd) {
              disabledInput(fd);
            });
          }

          return fg;
        });
        // console.log('UPDATE_DATA_SOURCE: ', filtersValues);
        this.$set(this, 'filtersDataSource', await Promise.all(queue));
        // console.timeEnd('FilterControl->updatedDataSources');
      },

      disableteste(lista, dependencia) {
        var lista2 = lista;

        lista2 = _.map(lista2, fd => {
          if (fd.dependency) {
            fd.enable = fg.fields.some(elem => elem.title == fd.dependency && elem.values && elem.values.value);
            if (fd.enable == false && fd.values && fd.values.value) {
              fd.values.value = null;
              fd.value = null;
            }
          }
          return fd;
        });
      }
      ,

      createValueFromField(field) {
        return {
          metadata: {
            name: this.getKey(field),
            text: field.text,
            value: field.value,
            properties: Object.keys((field.dataSource && field.dataSource[0]) || {}),
            all: true
          },
          ..._.reduce(
              field.dataSource,
              (acc, data) => {
                acc.text.push(data[field.text]);
                acc.value.push(data[field.value]);
                return acc;
              },
              {text: [], value: []}
          )
        };
      },

      getFiltersValues(all = true) {
        return new Promise((resolve, reject) => {
          // console.time('FilterControl->getFiltersValues');
          const filtersValues = _.reduce(
              this.filtersDataSource,
              (acc, fg) => {
                _(fg.fields).forEach(fd => {
                  const values = fd.values || {};
                  if (fd.title === values.text || values.text === 'Todas…' || values.text === 'Todos…') {
                    delete values.value;
                    acc[this.getKey(fd)] = values;
                  } else if (this.hasValue(values.value)) {
                    acc[this.getKey(fd)] = values;
                  } else if (
                      all &&
                      (!fd.required && (!this.hasValue(values.value) || _.size(values.value) === _.size(fd.dataSource))) &&
                      !fd.skipAll
                  ) {
                    acc[this.getKey(fd)] = this.createValueFromField(fd);
                  }
                });
                return acc;
              },
              {}
          );
          // console.log('FILTERS VALUES: ', filtersValues);
          // console.timeEnd('FilterControl->getFiltersValues');
          return resolve(filtersValues);
        });
      },

      async onSubmit() {
        if (this.isValid()) {
          const values = await this.getFiltersValues();

          this.filtersSummary = _(values)
              .filter(fd => (fd.metadata ? !fd.metadata.all : true) && _.size(fd.text))
              .map(fd => (_.isArray(fd.text) ? fd.text.join(', ') : fd.text))
              .value()
              .join(' > ');

          // Add extra values based on filtersData
          _(this.requestParams)
              .filter(p => !values[p])
              .forEach(p => {
                const filtersData = _(this.filtersDataSource)
                    .filter(fg => fg.data)
                    .map(fg => fg.data)
                    .flatten()
                    .filter(data => data[p])
                    .value();

                if (_.size(filtersData)) {
                  values[p] = {
                    text: p,
                    value: _(filtersData)
                        .filter(d =>
                            _(values)
                                .filter(val => val.value && val.metadata && _.some(val.metadata.properties, k => k === p))
                                .every(val =>
                                    _.isArray(val.value)
                                        ? _.some(val.value, v => this.normalize(d[val.metadata.value]) === this.normalize(v))
                                        : this.normalize(d[val.metadata.value]) === this.normalize(val.value)
                                )
                        )
                        .map(d => d[p])
                        .value()
                  };
                }
              });

          if (this.currentGroup) {
            values.group = {
              value: this.currentGroup,
              text: this.currentGroup
            };
          }

          // console.log('FILTER_CONTROL: ', values);
          this.updateFiltersValues(values);
          this.$emit('submit', values);
        }
      }
    },

    mounted() {
      // console.log('filterControl -> mounted', this.filtersValues);
      this.createFiltersGroup();
    },

    watch: {
      filtersCurrentGroup() {
        this.reload();
      },

      filtersCurrentCategory() {
        this.reload();
      },

      filtersData() {
        this.reload();
      }
    },

    components: {
      KendoDatepicker,
      KendoDropdown,
      KendoMultiselect,
      KendoSlider
    }
  };
</script>

<style lang="scss" scoped>
</style>
